import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import Brand from "@/components/Brand";
import { useAuth } from "@/lib/auth";
import { ArrowUpRight, LogIn, Menu, MapPin, Mail, Phone, Facebook, Youtube, Twitter, UserPlus, Handshake, Bell, Newspaper, CalendarDays, Shield, ShoppingBag } from "lucide-react";
import api from "@/lib/api";
import { MemberApplyDialog } from "@/components/overlays/MemberApplyDialog";
import { FollowDialog } from "@/components/overlays/FollowDialog";
import { SponsorProposalDialog } from "@/components/overlays/SponsorProposalDialog";

/**
 * Nav links used both on the desktop inline nav and inside the mobile
 * Sheet drawer. Extracted so they can't drift out of sync.
 */
const PUBLIC_NAV_LINKS = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/events", label: "Events", testid: "nav-events", icon: "CalendarDays" },
  { to: "/news", label: "News", testid: "nav-news", icon: "Newspaper" },
  { to: "/shop", label: "Shop", testid: "nav-shop", icon: "ShoppingBag" },
  { to: "/follow", label: "Follow", testid: "nav-follow", icon: "Bell" },
  { to: "/club", label: "Club", testid: "nav-club", icon: "Shield" },
  { to: "/apply", label: "Become a member", testid: "nav-become-member", icon: "UserPlus" },
  { to: "/sponsor", label: "Become a sponsor", testid: "nav-become-sponsor", icon: "Handshake" },
];

export default function PublicLayout({ children, hideHeader = false }) {
  const { user, isAdmin, isMember, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [landing, setLanding] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  // Overlay dialog state for header/mobile nav triggers
  const [showMemberApply, setShowMemberApply] = useState(false);
  const [showFollow, setShowFollow] = useState(false);
  const [showSponsorProposal, setShowSponsorProposal] = useState(false);

  useEffect(() => {
    api.get("/landing").then(({ data }) => setLanding(data)).catch(() => {});
  }, []);

  // Desktop header shrink on scroll
  useEffect(() => {
    const onScroll = () => {
      if (window.innerWidth >= 768) {
        setScrolled(window.scrollY > 60);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /** Scroll to top when clicking the header background (not buttons/links). */
  const handleHeaderClick = (e) => {
    const tag = e.target.closest("a, button, input, select, textarea, [role='button']");
    if (tag) return;                // ignore clicks on interactive elements
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Auto-close the mobile sheet on route change so back-nav / link taps
  // don't leave the drawer sitting open behind the new page.
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-[hsl(var(--bg))] overflow-x-hidden relative">
      {!hideHeader && (
        <>
        <header onClick={handleHeaderClick} className="fixed top-0 left-0 right-0 z-30 border-b border-[#132A6B] bg-[#1E3A8A] text-white transition-all duration-300 ease-out cursor-pointer">
          <div className={`ml-0 px-4 sm:px-6 lg:px-8 h-16 flex items-end justify-between gap-2 relative pb-3 transition-all duration-300 ease-out ${scrolled ? "md:h-16 md:pb-3" : "md:h-32 md:pb-4"}`}>
            {/* Logo + Est.1835 — bottom-aligned left */}
            <div className="flex items-end gap-2 sm:gap-3 shrink-0">
              {landing?.club_logo_url ? (
                <Link to="/" className="shrink-0" data-testid="header-club-logo">
                  <img
                    src={landing.club_logo_url}
                    alt="Club logo"
                    className={`h-10 sm:h-12 w-auto object-contain transition-all duration-300 ease-out ${scrolled ? "md:h-[3.25rem]" : "md:h-[7rem]"}`}
                    style={{ borderRadius: "50%" }}
                    data-testid="header-club-logo-img"
                  />
                </Link>
              ) : (
                <span><Brand compact /></span>
              )}
              {/* Est. 1835 — bottom-aligned with logo, hidden on desktop */}
              <span className="inline-flex items-end gap-1 md:hidden" style={{ color: "#C0C0C0" }} data-testid="header-est-year">
                <span className="text-sm font-semibold tracking-widest" style={{ lineHeight: 1 }}>Est.</span><span className="text-xl font-bold tracking-wider" style={{ lineHeight: 1 }}>1835</span>
              </span>
            </div>
              {/* Nav buttons — centered on the full page */}
              <nav className="hidden sm:flex items-end gap-5 sm:gap-6 absolute bottom-3 md:bottom-4" style={{ left: "50vw", transform: "translateX(-50%)" }}>
                <button
                  type="button"
                  onClick={() => setShowMemberApply(true)}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-white/80 transition-colors pb-0 border-b-2 border-transparent"
                  data-testid="nav-become-member"
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  Become a member
                </button>
                <button
                  type="button"
                  onClick={() => setShowSponsorProposal(true)}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-white/80 transition-colors pb-0 border-b-2 border-transparent"
                  data-testid="nav-become-sponsor"
                >
                  <Handshake className="h-3.5 w-3.5" />
                  Become a sponsor
                </button>
                <button
                  type="button"
                  onClick={() => setShowFollow(true)}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-white/80 transition-colors pb-0 border-b-2 border-transparent"
                  data-testid="nav-follow"
                >
                  <Bell className="h-3.5 w-3.5" />
                  Follow
                </button>
                <Link
                  to="/news"
                  className={`inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-white/80 transition-colors pb-0 border-b-2 ${loc.pathname === "/news" ? "border-white" : "border-transparent"}`}
                  data-testid="nav-news"
                >
                  <Newspaper className="h-3.5 w-3.5" />
                  News
                </Link>
                <Link
                  to="/events"
                  className={`inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-white/80 transition-colors pb-0 border-b-2 ${loc.pathname === "/events" ? "border-white" : "border-transparent"}`}
                  data-testid="nav-events"
                >
                  <CalendarDays className="h-3.5 w-3.5" />
                  Events
                </Link>
                <Link
                  to="/shop"
                  className={`inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-white/80 transition-colors pb-0 border-b-2 ${loc.pathname.startsWith("/shop") ? "border-white" : "border-transparent"}`}
                  data-testid="nav-shop"
                >
                  <ShoppingBag className="h-3.5 w-3.5" />
                  Shop
                </Link>
                <Link
                  to="/club"
                  className={`inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-white/80 transition-colors pb-0 border-b-2 ${loc.pathname === "/club" ? "border-white" : "border-transparent"}`}
                  data-testid="nav-club"
                >
                  <Shield className="h-3.5 w-3.5" />
                  Club
                </Link>
              </nav>
            <div className="flex items-center gap-1.5 sm:gap-2">
              {isMember && !isAdmin && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => nav("/m")}
                  data-testid="nav-member-portal"
                  className="hidden sm:inline-flex text-sm font-semibold text-white/85 hover:text-white min-h-[44px]"
                >
                  Members
                </Button>
              )}
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => nav("/a")}
                  data-testid="nav-admin-portal"
                  className="hidden sm:inline-flex text-sm font-semibold text-white/85 hover:text-white min-h-[44px]"
                >
                  Admin
                </Button>
              )}
              {user ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full hidden sm:inline-flex mr-0 md:mr-[200px] text-sm font-semibold text-white/85 hover:text-white border-white/30 min-h-[44px]"
                  onClick={() => { logout(); nav("/"); }}
                  data-testid="nav-logout"
                >
                  Sign out
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={() => nav("/login")}
                  className="rounded-full bg-[hsl(var(--fg))] text-[hsl(var(--bg))] hover:bg-[hsl(var(--fg))]/90 mr-0 md:mr-[200px] text-sm font-semibold min-h-[44px] min-w-[44px]"
                  data-testid="nav-members-button"
                >
                  <span className="hidden sm:inline">Members</span>
                  <LogIn className="ml-1 h-3.5 w-3.5 sm:hidden" />
                  <ArrowUpRight className="ml-1 h-3.5 w-3.5 hidden sm:inline-block" />
                </Button>
              )}

              {/* Mobile hamburger — opens a sheet with the same links + auth
                  shortcuts. Hidden ≥ md where the inline nav is visible. */}
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="sm:hidden h-11 w-11"
                    aria-label="Open menu"
                    data-testid="public-open-menu"
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[85vw] max-w-sm p-0 flex flex-col">
                  <div className="px-5 pt-6 pb-4 border-b border-border w-full flex justify-center">
                    <div className="w-fit">
                      <Brand />
                    </div>
                  </div>
                  <nav className="flex-1 overflow-y-auto px-4 py-4 space-y-0.5">
                    {PUBLIC_NAV_LINKS.map((l) => {
                      const IconMap = { CalendarDays, Newspaper, Bell, Shield, UserPlus, Handshake, ShoppingBag };
                      const Icon = l.icon ? IconMap[l.icon] : null;
                      // Overlay items: close drawer then open overlay
                      const overlayMap = {
                        "/apply": () => { setMobileOpen(false); setTimeout(() => setShowMemberApply(true), 150); },
                        "/sponsor": () => { setMobileOpen(false); setTimeout(() => setShowSponsorProposal(true), 150); },
                        "/follow": () => { setMobileOpen(false); setTimeout(() => setShowFollow(true), 150); },
                      };
                      if (overlayMap[l.to]) {
                        return (
                          <button
                            key={l.to}
                            type="button"
                            onClick={overlayMap[l.to]}
                            className="w-full flex items-center gap-3 rounded-lg px-3 py-3 min-h-[48px] text-[15px] font-medium text-foreground/80 hover:bg-[hsl(var(--secondary))] hover:text-foreground transition-colors"
                            data-testid={`mobile-${l.testid}`}
                          >
                            {Icon && <Icon className="h-5 w-5 shrink-0 text-[#4169E1]" />}
                            {l.label}
                          </button>
                        );
                      }
                      return (
                        <Link
                          key={l.to}
                          to={l.to}
                          className={`flex items-center gap-3 rounded-lg px-3 py-3 min-h-[48px] text-[15px] font-medium transition-colors ${loc.pathname === l.to ? "bg-[hsl(var(--secondary))] text-foreground font-semibold" : "text-foreground/80 hover:bg-[hsl(var(--secondary))] hover:text-foreground"}`}
                          data-testid={`mobile-${l.testid}`}
                        >
                          {Icon && <Icon className="h-5 w-5 shrink-0 text-[#4169E1]" />}
                          {l.label}
                        </Link>
                      );
                    })}
                    <div className="my-3 border-t border-border" />
                    {isMember && !isAdmin && (
                      <button
                        type="button"
                        onClick={() => { nav("/m"); setMobileOpen(false); }}
                        className="w-full flex items-center gap-3 rounded-lg px-3 py-3 min-h-[48px] text-[15px] font-medium text-foreground/80 hover:bg-[hsl(var(--secondary))] hover:text-foreground transition-colors"
                        data-testid="mobile-nav-member-portal"
                      >
                        Members portal
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => { nav("/a"); setMobileOpen(false); }}
                        className="w-full flex items-center gap-3 rounded-lg px-3 py-3 min-h-[48px] text-[15px] font-medium text-foreground/80 hover:bg-[hsl(var(--secondary))] hover:text-foreground transition-colors"
                        data-testid="mobile-nav-admin-portal"
                      >
                        Admin portal
                      </button>
                    )}
                    {!user && (
                      <>
                        <Link
                          to="/login"
                          className="flex items-center gap-3 rounded-lg px-3 py-3 min-h-[48px] text-[15px] font-medium text-foreground/80 hover:bg-[hsl(var(--secondary))] hover:text-foreground transition-colors"
                          data-testid="mobile-nav-member-signin"
                        >
                          <LogIn className="h-5 w-5 shrink-0 text-[#4169E1]" />
                          Member sign in
                        </Link>
                        <Link
                          to="/admin/login"
                          className="flex items-center gap-3 rounded-lg px-3 py-3 min-h-[48px] text-[15px] font-medium text-foreground/80 hover:bg-[hsl(var(--secondary))] hover:text-foreground transition-colors"
                          data-testid="mobile-nav-admin-signin"
                        >
                          <Shield className="h-5 w-5 shrink-0 text-[#4169E1]" />
                          Admin sign in
                        </Link>
                      </>
                    )}
                    {user && (
                      <button
                        type="button"
                        onClick={() => { logout(); nav("/"); setMobileOpen(false); }}
                        className="w-full flex items-center gap-3 rounded-lg px-3 py-3 min-h-[48px] text-[15px] font-medium text-foreground/80 hover:bg-[hsl(var(--secondary))] hover:text-foreground transition-colors"
                        data-testid="mobile-nav-logout"
                      >
                        Sign out
                      </button>
                    )}
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </header>
        <div className={`h-16 transition-all duration-300 ease-out ${scrolled ? "md:h-16" : "md:h-32"}`} />
        </>
      )}
      <main className="flex-1">{children}</main>
      <footer className="border-t border-[#132A6B] bg-[#1E3A8A] text-white py-6 sm:py-4">
        <div className="mx-auto max-w-7xl w-full px-5 sm:px-6 lg:px-8 flex flex-col gap-3 sm:gap-2 items-center text-center sm:text-left sm:flex-row sm:justify-between sm:flex-wrap text-xs text-white/70">
          <span className="text-sm sm:text-xs font-medium">© {new Date().getFullYear()} Derwent Cricket Club</span>
          {landing?.contact_email && (
            <a href={`mailto:${landing.contact_email}`} className="flex items-center gap-2 hover:text-white transition-colors min-h-[44px] text-sm sm:text-xs" data-testid="footer-contact-email">
              <Mail className="h-4 w-4 sm:h-3.5 sm:w-3.5" /> {landing.contact_email}
            </a>
          )}
          {landing?.contact_phone && (
            <a href={`tel:${landing.contact_phone}`} className="flex items-center gap-2 hover:text-white transition-colors min-h-[44px] text-sm sm:text-xs" data-testid="footer-contact-phone">
              <Phone className="h-4 w-4 sm:h-3.5 sm:w-3.5" /> {landing.contact_phone}
            </a>
          )}
          {landing?.location && (
            <span className="flex items-center gap-2 min-h-[44px] text-sm sm:text-xs" data-testid="footer-location">
              <MapPin className="h-4 w-4 sm:h-3.5 sm:w-3.5" /> {landing.location}
            </span>
          )}
          <div className="flex items-center gap-5 mt-1 sm:mt-0">
            {landing?.facebook_url && (
              <a href={landing.facebook_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors min-h-[44px]" data-testid="footer-facebook">
                <Facebook className="h-5 w-5 sm:h-3.5 sm:w-3.5" />
                <span className="sm:inline text-sm sm:text-xs">Facebook</span>
              </a>
            )}
            {landing?.facebook_private_url && (
              <a href={landing.facebook_private_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors min-h-[44px]" data-testid="footer-facebook-private">
                <Facebook className="h-5 w-5 sm:h-3.5 sm:w-3.5" />
                <span className="sm:inline text-sm sm:text-xs">Private Page</span>
              </a>
            )}
            {landing?.youtube_url && (
              <a href={landing.youtube_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors min-h-[44px]" data-testid="footer-youtube">
                <Youtube className="h-5 w-5 sm:h-3.5 sm:w-3.5" />
                <span className="sm:inline text-sm sm:text-xs">YouTube</span>
              </a>
            )}
            {landing?.twitter_url && (
              <a href={landing.twitter_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors min-h-[44px]" data-testid="footer-twitter">
                <Twitter className="h-5 w-5 sm:h-3.5 sm:w-3.5" />
                <span className="sm:inline text-sm sm:text-xs">X / Twitter</span>
              </a>
            )}
          </div>
        </div>
      </footer>
      {/* Overlay dialogs triggered from header/mobile nav */}
      <MemberApplyDialog open={showMemberApply} onClose={() => setShowMemberApply(false)} />
      <FollowDialog open={showFollow} onClose={() => setShowFollow(false)} />
      <SponsorProposalDialog open={showSponsorProposal} onClose={() => setShowSponsorProposal(false)} />
    </div>
  );
}
